/**
 * ProjectsGrid - Column definitions and grid configuration for the Projects table.
 *
 * Architecture (following EBTimeEntry's TimesheetReportGrid pattern):
 *   ProjectsPage (data fetching, filters, pagination state)
 *     └─ ProjectsGrid (column definitions, cell renderers)  ← this file
 *         └─ DataTableEnhanced (generic table rendering)
 *
 * All cell rendering logic that was previously in the monolithic renderColumnValue
 * switch statement in projects.tsx is now expressed as TanStack ColumnDef cell renderers.
 */

import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SensitiveData } from '@/components/sensitive-data';
import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  type ColumnSort,
} from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getHealthBadgeColor,
  formatStatus,
  getVarianceColor,
  calculateDaysRemaining,
  calculateGrossProfit,
  calculateForecastAtCompletion,
  formatProjectType,
  formatRelativeTime,
  getDaysRemainingColor,
  getProjectTypeColor,
  getWIPColor,
} from '@/lib/utils';
import { getProjectStatusColor } from '@/lib/status-colors';
import type { Project, Client, Employee, Task, Milestone, ResourceAllocation } from '@/types';
import { PROJECT_STATUS_LABELS } from '@/types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import type { PaginationMetadata } from '@/types/pagination';

// ── Types ────────────────────────────────────────────────────────────────

/** Reference data needed by cell renderers */
export interface ProjectsGridReferenceData {
  clients: Client[];
  employees: Employee[];
  tasks: Task[];
  milestones: Milestone[];
  resourceAllocations: ResourceAllocation[];
}

export interface ProjectsGridProps {
  /** Pre-sorted/paginated project data to render */
  data: Project[];
  /** Reference data for lookups in cell renderers */
  referenceData: ProjectsGridReferenceData;
  /** Column definitions from ColumnConfiguratorDrawer (controls visibility & order) */
  columnDefinitions: ColumnDefinition[];
  /** Multi-column sort state */
  columnSorts: ColumnSort[];
  /** Callback when sort state changes */
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  /** Callback when a project row is clicked */
  onRowClick: (project: Project) => void;
  /** Show loading skeleton */
  loading?: boolean;
  /** Show transition overlay */
  isTransitioning?: boolean;
  /** Number of skeleton rows when loading */
  pageSize?: number;
  /** Current search/filter state for empty message */
  hasActiveFilters?: boolean;
  /** Pagination metadata for pagination controls */
  pagination?: PaginationMetadata;
  /** Callback for page changes */
  onPageChange?: (page: number) => void;
  /** Callback for page size changes */
  onPageSizeChange?: (size: number) => void;
}

// ── Helper functions (extracted from projects.tsx) ───────────────────────

function getClientNames(project: Project, clients: Client[]): string {
  if (project.clientNames) return project.clientNames;
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const names = ids.map(
      (id) => clients.find((c) => c.id === parseInt(id, 10))?.name || 'Unknown',
    );
    return names.join(', ');
  }
  return 'Unknown';
}

function getClientCodes(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const codes = ids.map(
      (id) => clients.find((c) => c.id === parseInt(id, 10))?.code || '-',
    );
    return codes.join(', ');
  }
  return '-';
}

function getManagerName(managerId: number, employees: Employee[]): string {
  return employees.find((e) => e.id === managerId)?.name || 'Unknown';
}

function getClientContacts(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const contacts = ids
      .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.contactName)
      .filter(Boolean);
    return contacts.length > 0 ? contacts.join(', ') : '-';
  }
  return '-';
}

function getClientIndustries(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const industries = [
      ...new Set(
        ids
          .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.industry)
          .filter(Boolean),
      ),
    ];
    return industries.length > 0 ? industries.join(', ') : '-';
  }
  return '-';
}

function getOverdueTasks(projectId: number, tasks: Task[]): number {
  const now = new Date();
  return tasks.filter(
    (t) =>
      t.projectId === projectId &&
      t.status !== 'completed' &&
      new Date(t.dueDate) < now,
  ).length;
}

function getOverdueMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter(
    (m) => m.projectId === projectId && m.status === 'overdue',
  ).length;
}

function getNextMilestone(
  projectId: number,
  milestones: Milestone[],
): Milestone | undefined {
  const upcoming = milestones
    .filter((m) => m.projectId === projectId && m.status === 'pending')
    .sort(
      (a, b) =>
        new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    );
  return upcoming[0];
}

function getAllocatedResources(
  projectId: number,
  allocations: ResourceAllocation[],
): number {
  const uniqueEmployees = new Set(
    allocations
      .filter((a) => a.projectId === projectId)
      .map((a) => a.employeeId),
  );
  return uniqueEmployees.size;
}

function getResourceConflicts(
  projectId: number,
  allocations: ResourceAllocation[],
): number {
  return allocations.filter(
    (a) => a.projectId === projectId && a.conflictLevel !== 'none',
  ).length;
}

function getCompletedMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter(
    (m) => m.projectId === projectId && m.status === 'completed',
  ).length;
}

function getTotalMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter((m) => m.projectId === projectId).length;
}

function getUtilizationRate(project: Project): number {
  if (project.budgetHours === 0) return 0;
  return (project.actualHours / project.budgetHours) * 100;
}

function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// ── Column builder ───────────────────────────────────────────────────────

/**
 * Build the full set of TanStack column definitions for the Projects grid.
 * Each column ID matches the ColumnDefinition IDs from column-configurator-drawer.
 * Cell renderers use the provided reference data for lookups.
 */
function buildProjectColumns(
  ref: ProjectsGridReferenceData,
  statusLabels: Record<number, string>,
): ColumnDef<Project, unknown>[] {
  return [
    {
      id: 'project-name',
      accessorFn: (row) => row.name,
      header: 'Project Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.name}</div>
      ),
    },
    {
      id: 'project-code',
      accessorFn: (row) => row.code,
      header: 'Project Code',
      cell: ({ row }) => row.original.code || '-',
    },
    {
      id: 'client-code',
      accessorFn: (row) => getClientCodes(row, ref.clients),
      header: 'Client Code',
      cell: ({ row }) => getClientCodes(row.original, ref.clients),
    },
    {
      id: 'client-name',
      accessorFn: (row) => getClientNames(row, ref.clients),
      header: 'Client Name',
      cell: ({ row }) => getClientNames(row.original, ref.clients),
    },
    {
      id: 'client-contact',
      accessorFn: (row) => getClientContacts(row, ref.clients),
      header: 'Client Contact',
      cell: ({ row }) => getClientContacts(row.original, ref.clients),
    },
    {
      id: 'industry',
      accessorFn: (row) => getClientIndustries(row, ref.clients),
      header: 'Industry',
      cell: ({ row }) => getClientIndustries(row.original, ref.clients),
    },
    {
      id: 'account-manager',
      header: 'Account Manager',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'project-manager',
      accessorFn: (row) => getManagerName(row.projectManagerId, ref.employees),
      header: 'Project Manager',
      cell: ({ row }) =>
        getManagerName(row.original.projectManagerId, ref.employees),
    },
    {
      id: 'project-status',
      accessorFn: (row) => row.statusId,
      header: 'Project Status',
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={getProjectStatusColor(row.original.statusId)}
          disableHover
        >
          {statusLabels[row.original.statusId] || PROJECT_STATUS_LABELS[row.original.statusId] || formatStatus(String(row.original.statusId))}
        </Badge>
      ),
    },
    {
      id: 'health-indicator',
      accessorFn: (row) => row.health,
      header: 'Health Indicator',
      cell: ({ row }) => (
        <Badge
          className={getHealthBadgeColor(row.original.health)}
          disableHover
        >
          {row.original.health.toUpperCase()}
        </Badge>
      ),
    },
    {
      id: 'budget',
      accessorFn: (row) => row.budgetRevenue,
      header: 'Budget',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.budgetRevenue)}
        </SensitiveData>
      ),
    },
    {
      id: 'actual-cost',
      accessorFn: (row) => row.actualLabour + row.actualExpense,
      header: 'Actual Cost',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(
            row.original.actualLabour + row.original.actualExpense,
          )}
        </SensitiveData>
      ),
    },
    {
      id: 'revenue',
      accessorFn: (row) => row.actualRevenue,
      header: 'Revenue',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.actualRevenue)}
        </SensitiveData>
      ),
    },
    {
      id: 'wip-amount',
      accessorFn: (row) => row.wipAmount,
      header: 'WIP Amount',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <span className={getWIPColor(row.original.wipAmount)}>
            {formatCurrency(row.original.wipAmount)}
          </span>
        </SensitiveData>
      ),
    },
    {
      id: 'gross-profit',
      accessorFn: (row) =>
        calculateGrossProfit(
          row.actualRevenue,
          row.actualLabour,
          row.actualExpense,
        ),
      header: 'Gross Profit',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const gp = calculateGrossProfit(
          row.original.actualRevenue,
          row.original.actualLabour,
          row.original.actualExpense,
        );
        return (
          <SensitiveData>
            <span
              className={
                gp >= 0 ? 'text-success-600' : 'text-error-600'
              }
            >
              {formatCurrency(gp)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecast-at-completion',
      accessorFn: (row) =>
        calculateForecastAtCompletion(
          row.actualLabour,
          row.actualExpense,
          row.budgetLabour,
          row.budgetExpense,
          row.completionPercent,
        ),
      header: 'Forecast at Completion',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const eac = calculateForecastAtCompletion(
          row.original.actualLabour,
          row.original.actualExpense,
          row.original.budgetLabour,
          row.original.budgetExpense,
          row.original.completionPercent,
        );
        return (
          <SensitiveData>{formatCurrency(eac)}</SensitiveData>
        );
      },
    },
    {
      id: 'allocated-hours',
      accessorFn: (row) => row.budgetHours,
      header: 'Allocated Hours',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {row.original.budgetHours.toLocaleString()} hrs
        </SensitiveData>
      ),
    },
    {
      id: 'variance',
      accessorFn: (row) => row.variancePercent,
      header: 'Variance',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className={getVarianceColor(row.original.variancePercent)}>
          {row.original.variancePercent > 0 ? '+' : ''}
          {formatPercent(row.original.variancePercent)}
        </span>
      ),
    },
    {
      id: 'completion-percentage',
      accessorFn: (row) => row.completionPercent,
      header: 'Completion Percentage',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Progress
            value={row.original.completionPercent}
            className="h-2 w-20"
          />
          <span className="text-xs text-muted-foreground w-10 text-right">
            {formatPercent(row.original.completionPercent, 0)}
          </span>
        </div>
      ),
    },
    {
      id: 'margin',
      accessorFn: (row) => row.marginPercent,
      header: 'Margin',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatPercent(row.original.marginPercent)}
        </SensitiveData>
      ),
    },
    {
      id: 'project-type',
      accessorFn: (row) => row.type,
      header: 'Project Type',
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={getProjectTypeColor(row.original.type)}
          disableHover
        >
          {formatProjectType(row.original.type)}
        </Badge>
      ),
    },
    {
      id: 'phase',
      header: 'Phase/Stage',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'start-date',
      accessorFn: (row) => row.startDate,
      header: 'Start Date',
      cell: ({ row }) => formatDate(row.original.startDate),
    },
    {
      id: 'end-date',
      accessorFn: (row) => row.endDate,
      header: 'End Date',
      cell: ({ row }) => formatDate(row.original.endDate),
    },
    {
      id: 'days-remaining',
      accessorFn: (row) => calculateDaysRemaining(row.endDate),
      header: 'Days Remaining',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const days = calculateDaysRemaining(row.original.endDate);
        if (days === null) {
          return (
            <span className="text-muted-foreground text-sm">-</span>
          );
        }
        return (
          <span className={getDaysRemainingColor(days)}>
            {days > 0
              ? `${days} days`
              : days === 0
                ? 'Today'
                : 'Overdue'}
          </span>
        );
      },
    },
    {
      id: 'next-milestone',
      accessorFn: (row) => {
        const m = getNextMilestone(row.id, ref.milestones);
        return m?.name ?? '';
      },
      header: 'Next Milestone',
      cell: ({ row }) => {
        const milestone = getNextMilestone(
          row.original.id,
          ref.milestones,
        );
        if (!milestone) {
          return (
            <span className="text-muted-foreground text-sm">-</span>
          );
        }
        return (
          <div>
            <div className="font-medium text-sm">{milestone.name}</div>
            <div className="text-xs text-muted-foreground">
              {formatDate(milestone.dueDate)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'team-members',
      accessorFn: (row) =>
        getAllocatedResources(row.id, ref.resourceAllocations),
      header: 'Team Members',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getAllocatedResources(
          row.original.id,
          ref.resourceAllocations,
        );
        return count > 0
          ? `${count} member${count !== 1 ? 's' : ''}`
          : '-';
      },
    },
    {
      id: 'resource-conflicts',
      accessorFn: (row) =>
        getResourceConflicts(row.id, ref.resourceAllocations),
      header: 'Resource Conflicts',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const conflicts = getResourceConflicts(
          row.original.id,
          ref.resourceAllocations,
        );
        return conflicts > 0 ? (
          <Badge variant="destructive" disableHover>
            {conflicts}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">None</span>
        );
      },
    },
    {
      id: 'tasks-overdue',
      accessorFn: (row) => getOverdueTasks(row.id, ref.tasks),
      header: 'Overdue Tasks',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getOverdueTasks(row.original.id, ref.tasks);
        return count > 0 ? (
          <Badge variant="destructive" disableHover>
            {count}
          </Badge>
        ) : (
          <span className="text-success-600 text-sm">0</span>
        );
      },
    },
    {
      id: 'overdue-milestones',
      accessorFn: (row) => getOverdueMilestones(row.id, ref.milestones),
      header: 'Overdue Milestones',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getOverdueMilestones(
          row.original.id,
          ref.milestones,
        );
        return count > 0 ? (
          <Badge variant="destructive" disableHover>
            {count}
          </Badge>
        ) : (
          <span className="text-success-600 text-sm">0</span>
        );
      },
    },
    {
      id: 'last-updated',
      accessorFn: (row) => row.updatedAt,
      header: 'Last Updated',
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span className="text-sm">
                {formatRelativeTime(row.original.updatedAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {formatDate(row.original.updatedAt)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      id: 'milestones-completed',
      accessorFn: (row) => getCompletedMilestones(row.id, ref.milestones),
      header: 'Milestones Completed',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const completed = getCompletedMilestones(
          row.original.id,
          ref.milestones,
        );
        const total = getTotalMilestones(
          row.original.id,
          ref.milestones,
        );
        return total > 0 ? (
          <span>
            {completed} / {total}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
    },
    {
      id: 'profit-margin',
      accessorFn: (row) => row.marginPercent,
      header: 'Profit Margin',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatPercent(row.original.marginPercent)}
        </SensitiveData>
      ),
    },
    {
      id: 'billing-status',
      header: 'Billing Status',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'utilisation-rate',
      accessorFn: (row) => getUtilizationRate(row),
      header: 'Utilisation Rate',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const rate = getUtilizationRate(row.original);
        return (
          <span
            className={
              rate > 100
                ? 'text-red-600'
                : rate > 80
                  ? 'text-amber-600'
                  : 'text-green-600'
            }
          >
            {formatPercent(rate)}
          </span>
        );
      },
    },
    {
      id: 'duration',
      accessorFn: (row) =>
        calculateDuration(row.startDate, row.endDate),
      header: 'Duration',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const d = calculateDuration(
          row.original.startDate,
          row.original.endDate,
        );
        return `${d} day${d !== 1 ? 's' : ''}`;
      },
    },
    {
      id: 'risk-level',
      header: 'Risk Level',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'quality-score',
      header: 'Quality Score',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
  ];
}

// ── Component ────────────────────────────────────────────────────────────

export function ProjectsGrid({
  data,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  loading = false,
  isTransitioning = false,
  pageSize = 25,
  hasActiveFilters = false,
  pagination,
  onPageChange,
  onPageSizeChange,
}: ProjectsGridProps) {
  // Get dynamic status labels from context
  const { statusLabels } = useProjectStatuses();

  // Build full column set with cell renderers (memoised on reference data)
  const allColumns = useMemo(
    () => buildProjectColumns(referenceData, statusLabels),
    [referenceData, statusLabels],
  );

  // Filter and order columns to match the visible ColumnDefinitions
  const visibleColumnDefs = useMemo(() => {
    const visibleDefs = columnDefinitions
      .filter((cd) => cd.visible)
      .sort((a, b) => a.order - b.order);

    const columnMap = new Map(allColumns.map((c) => [c.id, c]));
    return visibleDefs
      .map((cd) => columnMap.get(cd.id))
      .filter((c): c is ColumnDef<Project, unknown> => c !== undefined);
  }, [columnDefinitions, allColumns]);

  // Build TanStack visibility state from ColumnDefinitions
  const columnVisibility = useMemo(
    () => columnDefinitionsToVisibility(columnDefinitions),
    [columnDefinitions],
  );

  // Empty state messages
  const emptyMessage = hasActiveFilters ? (
    <div className="space-y-2">
      <p className="text-lg font-medium">No projects found</p>
      <p className="text-sm">Try adjusting your filters or search term</p>
    </div>
  ) : (
    <div className="space-y-2">
      <p className="text-lg font-medium">No projects yet</p>
      <p className="text-sm">Get started by creating your first project</p>
    </div>
  );

  // Pagination content
  const paginationContent =
    !loading && pagination && pagination.totalItems > 0 && onPageChange ? (
      <PaginationControls
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSizeSelector={true}
        className="pt-4 border-t"
      />
    ) : null;

  return (
    <DataTableEnhanced<Project, unknown>
      columns={visibleColumnDefs}
      data={data}
      columnVisibility={columnVisibility}
      columnSorts={columnSorts}
      onColumnSortChange={onColumnSortChange}
      loading={loading}
      loadingRowCount={pageSize}
      isTransitioning={isTransitioning}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
      getRowId={(row) => String(row.id)}
      dataTour="project-list-table"
      paginationContent={paginationContent}
    />
  );
}
